import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Phone, Mail, Calendar, FileText, Plus, Clock, ShieldAlert, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { beneficiariosAPI } from '../services/api';

const Beneficiario = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [beneficiario, setBeneficiario] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [carencias, setCarencias] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBeneficiario();
  }, [id]);

  const loadBeneficiario = async () => {
    setLoading(true);
    try {
      const [benResponse, histResponse, carenciasResponse] = await Promise.all([
        beneficiariosAPI.get(id),
        beneficiariosAPI.getHistorico(id),
        beneficiariosAPI.getCarencias(id).catch(() => ({ data: [] })),
      ]);
      setBeneficiario(benResponse.data);
      setHistorico(histResponse.data);
      setCarencias(carenciasResponse.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados do beneficiario');
      navigate('/busca');
    }
    setLoading(false);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  const handleNovoAtendimento = () => {
    navigate('/kanban', {
      state: {
        abrirNovoAtendimento: true,
        beneficiario: beneficiario ? {
          id: beneficiario.id,
          nome_completo: beneficiario.nome_completo,
          cpf: beneficiario.cpf,
          cpf_mascarado: beneficiario.cpf_mascarado || beneficiario.cpf,
        } : null
      }
    });
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!beneficiario) {
    return <div className="empty-state">Beneficiario nao encontrado</div>;
  }

  return (
    <div className="beneficiario-container">
      {/* Header */}
      <div className="beneficiario-header">
        <div className="beneficiario-info">
          <h2>{beneficiario.nome_completo}</h2>
          <div className="beneficiario-details">
            <div className="detail-item">
              <div className="detail-label">CPF</div>
              <div className="detail-value">{beneficiario.cpf}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Matricula</div>
              <div className="detail-value">{beneficiario.numero_matricula || '-'}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Status</div>
              <div className="detail-value">
                <span className={`status-badge status-${beneficiario.status}`}>
                  {beneficiario.status}
                </span>
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">
                <Phone size={14} style={{ marginRight: '4px' }} />
                Telefone
              </div>
              <div className="detail-value">{beneficiario.telefone_principal || '-'}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">
                <Mail size={14} style={{ marginRight: '4px' }} />
                Email
              </div>
              <div className="detail-value">{beneficiario.email || '-'}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">
                <Calendar size={14} style={{ marginRight: '4px' }} />
                Data Nascimento
              </div>
              <div className="detail-value">{formatDate(beneficiario.data_nascimento)}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Plano</div>
              <div className="detail-value">{beneficiario.plano_contratado || '-'}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Vigencia</div>
              <div className="detail-value">
                {formatDate(beneficiario.data_vigencia_inicio)} ate{' '}
                {formatDate(beneficiario.data_vigencia_fim)}
              </div>
            </div>
          </div>
          {beneficiario.observacoes && (
            <div style={{ marginTop: '1rem', color: 'var(--gray-600)' }}>
              <strong>Observacoes:</strong> {beneficiario.observacoes}
            </div>
          )}
        </div>
        <button className="btn btn-success" onClick={handleNovoAtendimento}>
          <Plus size={18} />
          Novo Atendimento
        </button>
      </div>

      {/* Carências */}
      {carencias.length > 0 && (() => {
        const ativas = carencias.filter(c => !c.cumprida);
        const cumpridas = carencias.filter(c => c.cumprida);
        return (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', fontSize: '1rem', color: '#1a1a2e' }}>
              <ShieldAlert size={20} />
              Carências
              {ativas.length > 0 && (
                <span style={{ fontSize: '0.75rem', background: '#fce4ec', color: '#c62828', fontWeight: 700, padding: '2px 10px', borderRadius: '12px' }}>
                  {ativas.length} em vigor
                </span>
              )}
            </h3>

            {/* Ativas */}
            {ativas.length > 0 && (
              <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
                {ativas.map((c, i) => {
                  // Barra de progresso: quanto já passou
                  const dtInicio = c.dt_inicio ? c.dt_inicio.split('/').reverse().join('-') : null;
                  const dtVenc = c.dt_vencimento ? c.dt_vencimento.split('/').reverse().join('-') : null;
                  const totalDias = dtInicio && dtVenc
                    ? Math.max(1, Math.round((new Date(dtVenc) - new Date(dtInicio)) / 86400000))
                    : 1;
                  const diasPassados = totalDias - c.dias_restantes;
                  const pct = Math.min(100, Math.round((diasPassados / totalDias) * 100));

                  return (
                    <div key={i} style={{
                      background: '#fff8f8', border: '1px solid #ffcdd2',
                      borderRadius: '10px', padding: '12px 16px',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#b71c1c' }}>
                          <ShieldAlert size={14} style={{ marginRight: '5px', verticalAlign: 'text-bottom' }} />
                          {c.descricao}
                        </span>
                        <span style={{
                          background: '#c62828', color: '#fff',
                          borderRadius: '8px', padding: '2px 10px',
                          fontSize: '0.75rem', fontWeight: 700,
                        }}>
                          {c.dias_restantes} dias restantes
                        </span>
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#666', marginBottom: '8px' }}>
                        Início: <strong>{c.dt_inicio}</strong> &nbsp;·&nbsp; Vence: <strong>{c.dt_vencimento}</strong>
                      </div>
                      {/* Barra de progresso */}
                      <div style={{ background: '#ffcdd2', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${pct}%`, height: '100%',
                          background: pct < 50 ? '#ef9a9a' : pct < 80 ? '#e57373' : '#c62828',
                          borderRadius: '4px', transition: 'width 0.3s',
                        }} />
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '3px', textAlign: 'right' }}>
                        {pct}% cumprida
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Cumpridas (colapsável) */}
            {cumpridas.length > 0 && (
              <details style={{ cursor: 'pointer' }}>
                <summary style={{ fontSize: '0.82rem', color: '#666', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none' }}>
                  <ShieldCheck size={14} color="#2e7d32" />
                  <span>{cumpridas.length} carência{cumpridas.length > 1 ? 's' : ''} já cumprida{cumpridas.length > 1 ? 's' : ''}</span>
                </summary>
                <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
                  {cumpridas.map((c, i) => (
                    <div key={i} style={{
                      background: '#f1f8e9', border: '1px solid #c5e1a5',
                      borderRadius: '8px', padding: '8px 14px',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <span style={{ fontSize: '0.82rem', color: '#33691e', fontWeight: 500 }}>
                        <ShieldCheck size={13} style={{ marginRight: '5px', verticalAlign: 'text-bottom' }} />
                        {c.descricao}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#558b2f' }}>
                        Venceu em {c.dt_vencimento}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })()}

      {/* Historico de Atendimentos */}
      <div className="historico-section">
        <h3>
          <FileText size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Historico de Atendimentos
        </h3>

        {historico.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <p>Nenhum atendimento registrado</p>
          </div>
        ) : (
          <div className="historico-list">
            {historico.map((atend) => (
              <div
                key={atend.id}
                className="historico-item"
                onClick={() => navigate(`/atendimento/${atend.id}`)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="historico-protocolo">{atend.protocolo}</span>
                  <span className={`status-badge status-${atend.status}`}>
                    {atend.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="historico-data">
                  <Clock size={12} style={{ marginRight: '4px' }} />
                  {formatDateTime(atend.data_abertura)}
                  {atend.eh_transferido && (
                    <span style={{ marginLeft: '1rem', color: 'var(--primary)' }}>
                      (Transferido)
                    </span>
                  )}
                </div>
                <div className="historico-motivo">{atend.motivo}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Beneficiario;
